import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import {
  hasRequiredContact, PopupProfile, ProfileManagementService,
  ProfileUpdatedEvent, profilePopupErrorKey,
} from '../profile-management.service';

@Component({
  selector: 'app-profile-mobile-access',
  templateUrl: './profile-mobile-access.component.html',
  styleUrls: ['./profile-mobile-access.component.scss'],
})
export class ProfileMobileAccessComponent implements OnDestroy {
  @Input() profileDetails!: PopupProfile;
  @Input() siteId!: string;
  @Input() dialogRef?: { close: () => void };
  @Input() stepper = 18;
  @Input() fullEdit = false;
  @Output() stepperChange = new EventEmitter<number>();
  @Output() profileUpdated = new EventEmitter<ProfileUpdatedEvent>();
  isSubmitting = false;
  errorKey = '';
  invitationSent = false;
  private destroyed = new Subject<void>();

  constructor(private service: ProfileManagementService) {}

  get enabled(): boolean {
    return String(this.profileDetails?.isMobileCredentialEnabled) === 'true';
  }

  get invitePending(): boolean {
    return this.invitationSent || String(this.profileDetails?.invitationPending) === 'true';
  }

  get statusKey(): string {
    if (this.invitePending) return 'PROFILE_POPUP_MOBILE_INVITE_SENT';
    if (this.enabled) return 'PROFILE_POPUP_MOBILE_ENABLED';
    return 'PROFILE_POPUP_MOBILE_NOT_SET_UP';
  }

  get canInvite(): boolean {
    const profile = this.profileDetails;
    const email = profile?.email;
    const temporary = profile?.isTemporaryUser === true || this.fullEdit || Number(profile?.userTypeId || profile?.userType) === 9;
    return !!this.siteId && !!email && hasRequiredContact(profile) &&
      !this.enabled && String(profile?.isDeactivated) !== 'true' &&
      (temporary || this.invitePending);
  }

  sendInvite(): void {
    // Use the saved phone verbatim: backend rejects a phone different from the stored value.
    if (this.isSubmitting || !this.canInvite) return;
    this.errorKey = '';
    this.isSubmitting = true;
    const previousEmail = this.profileDetails.email!;
    this.service.mobileAccessInvite(this.siteId, previousEmail, this.profileDetails.phoneNumber!)
      .pipe(takeUntil(this.destroyed), finalize(() => this.isSubmitting = false))
      .subscribe({
        next: result => {
          this.profileDetails.email = result.email;
          this.profileDetails.stagedEmail = result.stagedEmail ?? null;
          if (result.phoneNumber != null) this.profileDetails.phoneNumber = result.phoneNumber;
          this.profileDetails.invitationPending = true;
          this.invitationSent = true;
          this.profileUpdated.emit({ previousEmail, email: result.email, profileDetails: this.profileDetails });
        },
        error: error => this.errorKey = profilePopupErrorKey(error, 'PROFILE_POPUP_INVITE_FAILED'),
      });
  }

  back(): void {
    if (!this.isSubmitting) this.stepperChange.emit(1);
  }

  close(): void {
    if (!this.isSubmitting) this.dialogRef?.close();
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }
}